import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { UserPlus, Check, X, MapPin, MessageSquare, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

function ConnectionCard({ conn, type, onAction }) {
  const other = conn.user;
  const navigate = useNavigate();
  if (!other) return null;
  const initials = other.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";

  return (
    <div className="bg-white border border-[#E6E6E6] rounded-md p-5 hover:shadow-sm transition-shadow" data-testid={`connection-${other._id}`}>
      <div className="flex items-start gap-3">
        <Link to={`/profile/${other._id}`}>
          <Avatar className="h-12 w-12 border-2 border-[#E6E6E6]">
            <AvatarFallback className="bg-[#074572] text-white font-medium">{initials}</AvatarFallback>
          </Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/profile/${other._id}`} className="font-medium text-zinc-900 hover:text-[#074572] text-sm">
            {other.name}
          </Link>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge variant="secondary" className="text-[10px] bg-[#074572]/5 text-[#074572] capitalize">{other.role}</Badge>
            {other.position && <span className="text-xs text-zinc-400">{other.position}</span>}
          </div>
          {other.location && (
            <p className="text-xs text-zinc-400 flex items-center gap-1 mt-1"><MapPin size={11} />{other.location}</p>
          )}
        </div>
      </div>
      <Separator className="bg-[#E6E6E6] my-3" />
      <div className="flex gap-2">
        {type === "pending" ? (
          <>
            <Button size="sm" className="flex-1 bg-[#F85037] hover:bg-[#e04530] text-white gap-1" onClick={() => onAction("accept", conn.id)} data-testid={`accept-${conn.id}`}>
              <Check size={14} /> Accept
            </Button>
            <Button size="sm" variant="outline" className="flex-1 border-[#E6E6E6] gap-1" onClick={() => onAction("reject", conn.id)} data-testid={`reject-${conn.id}`}>
              <X size={14} /> Decline
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" variant="outline" className="flex-1 border-[#E6E6E6] text-[#074572] gap-1" onClick={() => navigate(`/messages/${other._id}`)} data-testid={`msg-${other._id}`}>
              <MessageSquare size={14} /> Message
            </Button>
            <Link to={`/profile/${other._id}`} className="flex-1">
              <Button size="sm" variant="outline" className="w-full border-[#E6E6E6] text-zinc-600">Profile</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export default function ConnectionsPage() {
  const [accepted, setAccepted] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const [accRes, pendRes] = await Promise.all([
        api.get("/connections?status=accepted"),
        api.get("/connections/pending"),
      ]);
      setAccepted(accRes.data);
      setPending(pendRes.data);
    } catch { toast.error("Failed to load connections"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchConnections(); }, []);

  const handleAction = async (action, id) => {
    try {
      await api.put(`/connections/${id}/${action}`);
      toast.success(action === "accept" ? "Connection accepted!" : "Connection declined");
      fetchConnections();
    } catch { toast.error("Action failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6" data-testid="connections-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Network</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage your professional connections</p>
        </div>
        <Link to="/scout">
          <Button className="bg-[#F85037] hover:bg-[#e04530] text-white gap-2" data-testid="find-connections-btn">
            <UserPlus size={16} /> Find People
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="connections">
        <TabsList className="bg-transparent border-b border-[#E6E6E6] rounded-none w-full justify-start gap-6 px-0 h-auto pb-0">
          <TabsTrigger value="connections" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm" data-testid="tab-connections">
            Connections ({accepted.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm" data-testid="tab-pending">
            Pending ({pending.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="connections" className="mt-6">
          {loading ? (
            <p className="text-center py-8 text-zinc-400">Loading...</p>
          ) : accepted.length === 0 ? (
            <div className="text-center py-16 text-zinc-400">
              <Users size={40} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">No connections yet</p>
              <p className="text-sm mt-1">Start building your football network</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {accepted.map(c => <ConnectionCard key={c.id} conn={c} type="accepted" onAction={handleAction} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="pending" className="mt-6">
          {pending.length === 0 ? (
            <p className="text-center py-16 text-zinc-400">No pending requests</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pending.map(c => <ConnectionCard key={c.id} conn={c} type="pending" onAction={handleAction} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
